<template>
  <div class="cards-lists">
    <div v-for="cardType in typesByCards" :key="cardType.id" class="cards-list">
      <div
        class="add-card-type-card add-card-type-card-empty"
        :class="{
          'add-card-type-card-disabled': !event || isCardTypeDisabled(cardType, cardsByTypes)
        }"
      >
        <v-tooltip location="top" open-delay="100" max-width="350" :disabled="!event">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="add-card-type-card-plus-button-wrapper">
              <v-btn
                size="small"
                icon="mds:add"
                :disabled="$store.state.isWorkflowDisabled"
                class="add-card-type-card-plus-button"
                :style="'background-color:' + getBackgroundColour(cardType)"
                @click="callFnIfNotDisabled(cardType,() => submitCardForm(event.id, cardType.id, '<p>New card</p>'))"
              />
            </div>
          </template>
          {{ getAddButtonTooltip(cardType, 'Add one card') }}
        </v-tooltip>
        <h4 class="add-card-type-card-title text-truncate">
          {{ cardType.title }}
          {{ (cardsByTypes[cardType.id]?.length > 2) ? '(' + cardsByTypes[cardType.id].length + ')' : '' }}
        </h4>
        <v-spacer class="add-card-type-card-spacer"/>
        <v-tooltip location="top" open-delay="100" max-width="350" :disabled="!event">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="add-card-type-card-ask-ai-button-wrapper">
              <v-btn
                icon="mds:temp-preferences-custom"
                size="small"
                :disabled="$store.state.isWorkflowDisabled"
                class="add-card-type-card-ask-ai-button"
                @click="callFnIfNotDisabled(cardType, ()=>$emit('add-card-ai', event.id, cardType.id))"
              />
            </div>
          </template>

          {{ getAddButtonTooltip(cardType, 'Add one card with AI') }}
        </v-tooltip>
      </div>

      <draggable
        v-model="cardsByTypes[cardType.id]"
        group="drop-target"
        :data-type-id="cardType.id"
        :disabled="$store.state.isWorkflowDisabled || isCardTypeDisabled(cardType, cardsByTypes)"
        :sort="true"
        @add="onDraggableAdd($event)"
        @update="onDraggableUpdate(cardType.id, $event)"
      >
        <card-description
          v-for="(item, index) in listCards(cardType.id)"
          class="mb-6"
          :class="{'focused': item.id === focusCard}"
          :data-card-id="item.id"
          :key="item.id"
          :item="item"
          :event="event"
          :showAi="true"
          :show-as-invisible="isCardTypeDisabled(cardType, cardsByTypes) && index > 0"
          :hideReplies="true"
          :show-comments-count="true"
          :showEntitySelector="true"
          :expandOnHover="true"
          compact-replies
          hideOpenInSidebar
          @keydown.stop=""
          @ask-for-reply="onAskForReply"
          @open-replies="$emit('show-comments', { card: item, index: index + 1 })"
        >
          <card-description-field
            :item="item"
            :focus-card="focusCard"
            @move-focus="moveFocus"
            :scrollIntoViewArg="false"
          ></card-description-field>
        </card-description>
      </draggable>
      <div
        v-if="!showMoreByType[cardType.id] && cardsByTypes[cardType.id]?.length > 2"
        class="d-flex justify-end mb-6"
        style="cursor: pointer;"
        @click="showMoreByType[cardType.id] = true"
      >
        <span class="pt-1">Show more</span> <v-icon>mds:keyboard_double_arrow_down</v-icon>
      </div>
      <div
        v-if="showMoreByType[cardType.id] && cardsByTypes[cardType.id]?.length > 2"
        class="d-flex justify-end mb-6"
        style="cursor: pointer;"
        @click="showMoreByType[cardType.id] = false"
      >
        <span class="pt-1">Show less</span> <v-icon>mds:keyboard_double_arrow_up</v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import { VueDraggableNext } from 'vue-draggable-next'
import workflowApi from '@/api/workflowApi'
import cardHelpers from '@/helpers/cardHelpers'
import { EventBus } from '@/event-bus/event-bus.js'
import cardTypesApi from '@/api/cardTypesApi'
import CardDescription from '@/components/CardDescription'
import CardDescriptionField from '@/components/CardDescriptionField'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { mapState } from 'vuex'
import { DomainModelRole } from '@/constants/cardTypes'
import { stripHtml } from '@/utils/sanitize'

export default {
  mixins: [diagramHelpers],
  components: {
    draggable: VueDraggableNext,
    CardDescription,
    CardDescriptionField
  },

  props: {
    typesToDisplayInDiagram: Array,
    event: Object,
    cards: {
      type: Array,
      default () {
        return []
      }
    },
    showDone: Boolean
  },

  data () {
    return {
      showMoreByType: {},
      dialogOpen: false,
      pinDialog: false,
      cardsByTypes: {},
      showComments: null,
      textAreaFormLoading: false,
      focusCard: null,
      timer: null,
      tab: 'cards'
    }
  },

  computed: {
    ...mapState({
      cardTypes: state => state.cardTypes.cardTypes
    }),
    typesByCards () {
      return this.typesToDisplayInDiagram || []
    },
    isDecisionEvent () {
      return this.event?.type === 'bpmn:ExclusiveGateway'
    },
    hasDomainModelRole () {
      return (cardType) => Boolean(cardType?.domainModelRole)
    },

    attachments () {
      if (!this.event?.cards) {
        return []
      }

      return this.event.cards.reduce((acc, { attachments }) => {
        if (attachments) {
          acc.push(...attachments)
        }
        return acc
      }, [])
    }
  },

  watch: {
    cards (newVal) {
      this.computeCardsByType(newVal)
    }
  },

  mounted () {
    this.computeCardsByType(this.cards)
    EventBus.$on('open-card-on-event', item => {
      if (item.eventId === this.event.id) {
        this.dialogOpen = true
        this.pinDialog = true
      }
    })
    EventBus.$on('pin-dialog', item => {
      if (item.eventId === this.event?.id) {
        this.pinDialog = true
      }
    })
  },

  beforeUnmount () {
    EventBus.$off('open-card-on-event')
    EventBus.$off('pin-dialog')
  },

  methods: {
    listCards (cardTypeId) {
      const cards = this.cardsByTypes[cardTypeId] ? [...this.cardsByTypes[cardTypeId]] : []
      if (this.showMoreByType[cardTypeId]) {
        return cards
      }

      return cards.slice(0, 2)
    },
    isCardTypeDisabled (cardType, cardsByTypes) {
      if (!cardType) {
        return false
      }

      return [
        DomainModelRole.AGGREGATE_ROOT,
        DomainModelRole.COMMAND,
        DomainModelRole.DOMAIN_EVENT
      ].includes(cardType.domainModelRole) && cardsByTypes?.[cardType.id]?.length > 0
    },
    getAddButtonTooltip (cardType, defaultText) {
      if (this.isCardTypeDisabled(cardType, this.cardsByTypes)) {
        return `Use max 1 card of type ${cardType.title} per event.`
      }
      if (this.isDecisionEvent && this.hasDomainModelRole(cardType)) {
        return `${cardType.title} cards on decisions won't be part of the domain model and will be missing from the spec.`
      }

      return defaultText
    },
    onAskForReply (event) {
      this.$emit('ask-for-reply', Object.assign({}, event, { node: this.event }))
    },
    moveFocus (_, target) {
      target.firstChild.blur()
    },

    async submitCardForm (eventId, cardTypeId, text) {
      this.textAreaFormLoading = true
      const res = await workflowApi.createCard(this.$route.params.id, eventId, {
        cardTypeId,
        description: text
      })
      this.showMoreByType[cardTypeId] = true

      this.textAreaFormLoading = false
      if (res?.cards?.length) {
        this.$nextTick(() => {
          this.focusCard = res.cards[0].id

          if (res.cards[0].id && cardTypeId === this.getQueryCardType(this.cardTypes)?.id) {
            EventBus.$emit('define-read-model-table', { type: 'example', cardId: res.cards[0].id })
          }
        })
      }
    },

    getBackgroundColour (cardType) {
      return cardTypesApi.getBackgroundColour(cardType)
    },

    openDialog () {
      this.dialogOpen = true
    },

    computeCardsByType (allCards) {
      const typeCards = {}

      if (!allCards) {
        return {}
      }

      // add all types
      this.typesToDisplayInDiagram.forEach(cardType => {
        typeCards[cardType.id] = []
      })

      allCards
        .filter(({ done }) => this.showDone === Boolean(done))
        .forEach(card => {
          const newCard = { ...card }
          newCard.eventId = this.event.id

          const { cardType } = card

          if (!cardType) {
            return
          }

          if (!typeCards[cardType.id]) {
            typeCards[cardType.id] = []
          }

          typeCards[cardType.id].push(newCard)
        })

      Object.values(typeCards).forEach(cards => cardHelpers.sortByReleaseAndSortKey(cards))

      this.cardsByTypes = typeCards
    },

    onDraggableAdd (ev) {
      const newCardTypeId = ev.to.dataset.typeId
      const updatedCard = cardHelpers.moveCard(ev, this.event.cards, this.cardsByTypes, newCardTypeId)

      const oldCardType = updatedCard.cardType
      const newCardType = this.cardTypes.find((ct) => ct.id === newCardTypeId)
      const isOldTagType = oldCardType?.domainModelRole === DomainModelRole.TAG
      const isNewTagType = newCardType?.domainModelRole === DomainModelRole.TAG

      if (isOldTagType && !isNewTagType) {
        const tags = updatedCard.tags || []
        updatedCard.description = tags.length > 0 ? JSON.stringify(tags) : ''
        updatedCard.tags = []
      } else if (!isOldTagType && isNewTagType) {
        const description = stripHtml(updatedCard.description || '')
        try {
          const parsed = JSON.parse(description)
          if (Array.isArray(parsed)) {
            updatedCard.tags = parsed
          } else {
            updatedCard.tags = []
          }
        } catch {
          updatedCard.tags = []
        }
        updatedCard.description = ''
      }

      this.$emit('update-card', updatedCard)
    },

    onDraggableUpdate (cardTypeId, ev) {
      this.$emit('update-card', cardHelpers.moveCard(ev, this.event.cards, this.cardsByTypes, cardTypeId))
    },

    callFnIfNotDisabled (cardType, fn) {
      if (!!this.event && !this.isCardTypeDisabled(cardType, this.cardsByTypes)) {
        fn()
      }
    }
  }
}
</script>

<style scoped>
.add-card-type-card {
  display: flex;
  border-radius: 4px;
  align-items: center;
  margin-bottom: 12px;
  background-color: var(--light-1, #FCFCFC);

  .add-card-type-card-title {
    font-size: 14px;
    font-weight: 500;
    margin-right: 8px;
  }

  .add-card-type-card-plus-button,
  .add-card-type-card-ask-ai-button {
    :deep(.v-icon) {
      --v-icon-size-multiplier: 0.9
    }
  }

  &.add-card-type-card-empty {
    padding: 6px 12px;
    margin-bottom: 8px;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);

    .add-card-type-card-title {
      order: 2;
      margin-left: 8px;
      color: var(--text-dark, #1F2833);
    }

    .add-card-type-card-spacer {
      order: 3
    }

    .add-card-type-card-plus-button-wrapper {
      order: 1;

      .add-card-type-card-plus-button {
        color: var(--light-1, #FCFCFC);
        width: 20px;
        height: 20px;
        font-size: 12px;
      }
    }

    .add-card-type-card-ask-ai-button-wrapper {
      order: 4;

      .add-card-type-card-ask-ai-button {
        color: var(--text-dark, #1F2833);
      }
    }

  }

  &.add-card-type-card-disabled {
    opacity: 0.5;
    cursor: not-allowed;

    :deep(.v-btn) {
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}

.focused {
  animation: pulse 1s forwards;
}

@keyframes pulse {
  0% {
    filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0));
  }
  50%{
    filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.5));
  }
  100% {
    filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0));
  }
}

.cards-list {
  width: 100%;
}

.create-form {
  border-width: 1px;
  border-top-width: 8px;
  border-style: solid;
}

.column-title + .create-form {
  margin-top: 0 !important;
}

.cards-dialog-content.v-card--link::before {
  display: none;
}

.cards-dialog-content {
  cursor: default;
}
</style>

<style>
.small-font {
  font-size: 12px;
}

.small-font .v-label {
  font-size: 12px;
}
</style>
